import React from 'react';
import { BarChart2, Zap, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { usePlan } from '../context/PlanContext';
import { useRawReportData } from '../hooks/useRawReportData';
import { useInsightReport } from '../hooks/useInsightReport';

/* ── Signal chip config ──────────────────────────────────────────────────── */
const SIGNAL_CONFIG = {
  isOnTrack:           { label: 'On Track',          variant: 'positive' },
  hasStrongGross:      { label: 'Strong Gross',       variant: 'positive' },
  hasApptLeverage:     { label: 'Appt Leverage',      variant: 'positive' },
  isActivityHeavy:     { label: 'High Activity',      variant: 'neutral'  },
  isConversionLight:   { label: 'Low Conversion',     variant: 'negative' },
  hasConsistencyIssue: { label: 'Inconsistent Days',  variant: 'negative' },
  isBackLoaded:        { label: 'Back-Loaded',        variant: 'negative' },
  hasFollowUpGap:      { label: 'Follow-Up Gap',      variant: 'negative' },
};

/* ── Small sub-components ───────────────────────────────────────────────── */

function ProseSection({ label, text, isLoading }) {
  return (
    <div className="ip-card">
      <div className="ip-label">{label}</div>
      {isLoading ? (
        <div className="ip-prose-loading">
          <div className="ip-prose-loading-dot" />
          <div className="ip-prose-loading-dot" />
          <div className="ip-prose-loading-dot" />
          <span>AI is analyzing…</span>
        </div>
      ) : (
        <div className="ip-prose-block">{text}</div>
      )}
    </div>
  );
}

function ForecastCard({ scenario, className }) {
  const vsGoal = scenario.vsGoal;
  const vgoalClass = vsGoal > 0 ? 'above' : vsGoal < 0 ? 'below' : 'even';
  const vgoalText = vsGoal > 0 ? `+${vsGoal} vs goal`
                  : vsGoal < 0 ? `${vsGoal} vs goal`
                  : 'Goal';

  return (
    <div className={`ip-forecast-card ${className || ''}`}>
      <div className="ip-forecast-card-label">{scenario.label}</div>
      <div className="ip-forecast-units">{scenario.units}</div>
      <div className="ip-forecast-comm">${scenario.commission.toLocaleString()}</div>
      <span className={`ip-forecast-vgoal ${vgoalClass}`}>{vgoalText}</span>
      {scenario.dailyUnitsNeeded != null && (
        <div className="ip-forecast-daily">{scenario.dailyUnitsNeeded}/day needed</div>
      )}
    </div>
  );
}

/* ── Main panel ─────────────────────────────────────────────────────────── */

export default function InsightsPanel() {
  const { collapsedSections, toggleSection } = useAppContext();
  const { isPro } = usePlan();

  const rawData = useRawReportData();
  const {
    insightPacket,
    prose,
    isProseLoading,
    usedFallback,
    hasData,
    error,
  } = useInsightReport(rawData);

  if (!isPro) return null;

  const collapsed = !!collapsedSections['dashboard-insights'];

  return (
    <div className="section-card">
      {/* ── Header ── */}
      <div
        className="section-hdr section-hdr-toggle"
        onClick={() => toggleSection('dashboard-insights')}
      >
        <h2><BarChart2 size={18} /> AI Performance Insights</h2>
        <div className="hdr-right">
          <span className={`section-chevron ${collapsed ? 'chevron-collapsed' : ''}`}>▼</span>
        </div>
      </div>

      {/* ── Collapsible body ── */}
      <div className={`section-collapse-content ${collapsed ? 'section-collapsed' : ''}`}>

        {/* Error state */}
        {error && (
          <div className="ip-empty">
            <AlertTriangle size={28} style={{ color: '#ff6b6b' }} />
            <div className="ip-empty-desc" style={{ color: 'rgba(255,107,107,.8)' }}>
              {error}
            </div>
          </div>
        )}

        {/* No-data state */}
        {!error && !hasData && (
          <div className="ip-empty">
            <div className="ip-empty-icon"><BarChart2 size={32} /></div>
            <div className="ip-empty-desc">
              Log some sales and activity this month to unlock your AI performance insights.
            </div>
          </div>
        )}

        {/* Insights ready */}
        {!error && hasData && insightPacket && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Archetype */}
            <div className="ip-archetype">
              <div className="ip-archetype-top">
                <span className="ip-archetype-badge">
                  {insightPacket.archetype?.id?.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="ip-archetype-label">{insightPacket.archetype?.label}</div>
              <div className="ip-archetype-summary">{insightPacket.archetype?.summary}</div>
            </div>

            {/* Performance Signals */}
            {insightPacket.signals && (
              <div className="ip-card">
                <div className="ip-label">Performance Signals</div>
                <div className="ip-signals">
                  {Object.entries(insightPacket.signals)
                    .filter(([, val]) => val === true)
                    .map(([key]) => {
                      const cfg = SIGNAL_CONFIG[key];
                      if (!cfg) return null;
                      const Icon = cfg.variant === 'positive' ? TrendingUp
                                 : cfg.variant === 'negative' ? TrendingDown
                                 : null;
                      return (
                        <span key={key} className={`ip-signal-chip ${cfg.variant}`}>
                          {Icon && <Icon size={11} />}
                          {cfg.label}
                        </span>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Executive Summary */}
            <ProseSection
              label="Executive Summary"
              text={prose?.executiveSummary}
              isLoading={isProseLoading || (!prose?.executiveSummary)}
            />

            {/* Performance Story */}
            {(prose?.performanceStory || isProseLoading) && (
              <ProseSection
                label="Performance Story"
                text={prose?.performanceStory}
                isLoading={isProseLoading || (!prose?.performanceStory)}
              />
            )}

            {/* Bottleneck */}
            <div className="ip-bottleneck">
              <div className="ip-bottleneck-title">
                <AlertTriangle size={15} />
                {insightPacket.bottleneck?.title}
              </div>
              <div className="ip-bottleneck-diagnosis">
                {isProseLoading
                  ? insightPacket.bottleneck?.diagnosis
                  : (prose?.bottleneckNarrative || insightPacket.bottleneck?.diagnosis)}
              </div>
              {insightPacket.bottleneck?.lever && (
                <div className="ip-lever">
                  <Zap size={13} className="ip-lever-icon" />
                  {prose?.coachingNote || insightPacket.bottleneck.lever}
                </div>
              )}
            </div>

            {/* Forecast grid */}
            <div className="ip-card">
              <div className="ip-label">Month-End Forecast</div>
              <div className="ip-forecast-grid">
                <ForecastCard
                  scenario={insightPacket.forecast.baseline}
                  className=""
                />
                <ForecastCard
                  scenario={insightPacket.forecast.optimistic}
                  className="ip-fc-optimistic"
                />
                <ForecastCard
                  scenario={insightPacket.forecast.goal}
                  className="ip-fc-goal"
                />
              </div>
              {isProseLoading || prose?.forecastNarrative ? (
                <div style={{ marginTop: 10 }}>
                  <ProseSection
                    label=""
                    text={prose?.forecastNarrative}
                    isLoading={isProseLoading || (!prose?.forecastNarrative)}
                  />
                </div>
              ) : null}
            </div>

            {/* Precision Actions */}
            {insightPacket.actions?.length > 0 && (
              <div className="ip-card">
                <div className="ip-label">Precision Actions</div>
                <div className="ip-actions">
                  {insightPacket.actions.map((action, i) => (
                    <div key={i} className="ip-action-item">
                      <div className="ip-action-num">{i + 1}</div>
                      <div className="ip-action-body">
                        <div className="ip-action-cat">{action.category}</div>
                        <div className="ip-action-text">{action.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback indicator */}
            {usedFallback && (
              <div style={{ textAlign: 'center' }}>
                <span className="ip-fallback-badge">⚡ AI unavailable — template analysis shown</span>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
